import { css } from '@emotion/react'
import { Card, colors, CompanyLogo, font, mq } from 'plume-ui'
import * as React from 'react'
import { useUser } from '../../../contexts/AuthContext'
import { formatDate } from '@/lib/date'
import { formatWithComma, thousandToMillion } from '@/lib/number'
import { isEnterpriseWithTrial } from '@/lib/user'
import { CompanyFull } from '../../../types'
import { NumberWithUnit } from '../../atoms/NumberWIthUnit'
import { TagButton, TagButtonGroup } from '../../atoms/TagButton'
import { FormattedDate } from '../../atoms/FormattedDate'

type Props = {
  company: CompanyFull
}

export const CompanyCard: React.FC<Props> = (props) => {
  const user = useUser()

  return (
    <Card className="company-item">
      <Card.Content>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            ${mq.extraLarge} {
              flex-direction: row;
            }
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              ${mq.untilLarge} {
                margin-bottom: 1rem;
              }
            `}
          >
            <a href={props.company.path} target="_blnak">
              <CompanyLogo
                size="large"
                name={props.company.name}
                src={props.company.imageUrl}
                css={css`
                  margin-right: 1rem;
                `}
              />
            </a>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                ${mq.extraLarge} {
                  display: none;
                }
              `}
            >
              <Name path={props.company.path}>{props.company.name}</Name>
              <ResearchStatus
                css={css`
                  margin-top: 5px;
                `}
              >
                {props.company.researchStatus}
              </ResearchStatus>
            </div>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              flex: 1;
              padding-top: 5px;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;

                ${mq.untilLarge} {
                  display: none;
                }
              `}
            >
              <Name path={props.company.path}>{props.company.name}</Name>
              <ResearchStatus
                css={css`
                  margin-left: 3.5rem;
                `}
              >
                {props.company.researchStatus}
              </ResearchStatus>
            </div>
            <div
              css={css`
                font-size: 14px;
                line-height: 1.4;
                margin-top: 7px;
                max-height: calc(14px * 1.4 * 2);
                -webkit-line-clamp: 2;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                overflow: hidden;
              `}
            >
              {props.company.description}
            </div>
            <TagButtonGroup
              css={css`
                margin-top: 6px;
              `}
            >
              {props.company.tags.map((tag, i) => (
                <TagButton key={i} tag={tag} />
              ))}
            </TagButtonGroup>
          </div>
          <div
            css={css`
              flex: 1;
              display: flex;
              flex-direction: column;
              margin-top: 1rem;
              ${mq.extraLarge} {
                flex-direction: row;
                padding: 0 1rem;
              }
            `}
          >
            <NumberSectionColumn>
              <div>
                <LabelTag name="スピーダ調達シリーズ" />
                <span>{props.company.series || '-'}</span>
              </div>
              <div>
                <LabelTag
                  name={
                    props.company.public
                      ? 'IPO時 時価総額(初値)'
                      : `調達後評価額\n(${props.company.valuationAnnotation})`
                  }
                />
                <span>
                  {props.company.valuation && (
                    <>
                      <NumberWithUnit unit="百万円">
                        {formatWithComma(
                          props.company.public
                            ? props.company.valuation
                            : thousandToMillion(props.company.valuation)
                        )}
                      </NumberWithUnit>
                      {!props.company.public &&
                        props.company.lastValuationDate && (
                          <ExtraValue>{`${formatDate(
                            props.company.lastValuationDate,
                            'yyyy/MM/dd'
                          )}時点`}</ExtraValue>
                        )}
                    </>
                  )}
                </span>
              </div>
              {isEnterpriseWithTrial(user) && (
                <div>
                  <LabelTag name="総調達額" />
                  {props.company.totalFundRaise && (
                    <NumberWithUnit unit="千円">
                      {formatWithComma(props.company.totalFundRaise)}
                    </NumberWithUnit>
                  )}

                  {!props.company.public &&
                    props.company.totalFundRaise &&
                    props.company.totalFundRaiseDate && (
                      <ExtraValue>{`${formatDate(
                        props.company.totalFundRaiseDate,
                        'yyyy/MM/dd'
                      )}時点`}</ExtraValue>
                    )}
                </div>
              )}
            </NumberSectionColumn>
            <NumberSectionColumn>
              <div>
                <LabelTag name="設立" />
                <span>
                  {props.company.established ? (
                    <FormattedDate>{props.company.established}</FormattedDate>
                  ) : (
                    ''
                  )}
                </span>
              </div>
              <div>
                <LabelTag
                  name={props.company.public ? 'IPO時 従業員数' : '従業員数'}
                />
                {props.company.employees && (
                  <NumberWithUnit unit="人">
                    {formatWithComma(props.company.employees)}
                  </NumberWithUnit>
                )}
              </div>
            </NumberSectionColumn>
          </div>
        </div>
      </Card.Content>
    </Card>
  )
}

const Name: React.FC<{
  children: string
  path: string
  className?: string
}> = (props) => {
  return (
    <a href={props.path} target="_blank" className={props.className}>
      <span
        css={css`
          font-weight: ${font.weight.bold};
          color: ${colors.black30};
        `}
      >
        {props.children}
      </span>
    </a>
  )
}

const ResearchStatus: React.FC<{
  children: string | null
  className?: string
}> = (props) => {
  if (props.children === '調査継続') {
    return null
  }

  return (
    <span
      css={css`
        font-size: 13px;
        color: ${colors.textLight};
      `}
      className={props.className}
    >
      {props.children}
    </span>
  )
}

const LabelTag: React.FC<{ name: string }> = (props) => {
  return (
    <p
      css={css`
        margin: 0;
        padding: 3px 10px;
        display: inline-block;
        color: ${colors.black10};
        background-color: ${colors.gray40};
        border-color: ${colors.gray40};
        border-radius: 3px;
        width: 130px;
        text-align: center;
        font-size: 11px;
        margin-right: 10px;
        white-space: pre;

        ${mq.untilLarge} {
          background-color: transparent;
          border-color: transparent;
          font-size: 14px;
          font-weight: ${font.weight.bold};
          margin-right: 1.5rem;
          text-align: left;
          width: 160px;
        }
      `}
    >
      {props.name}
    </p>
  )
}

type NumberSectionColumnProps = {
  children: React.ReactNode
}

const NumberSectionColumn: React.FC<NumberSectionColumnProps> = (props) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        flex: 1;
        white-space: nowrap;
        > div {
          margin-bottom: 10px;
          margin-right: 5px;
        }

        ${mq.untilLarge} {
          justify-content: space-evenly;
          > div {
            margin-bottom: 0.4rem;
            display: flex;
          }
        }
      `}
    >
      {props.children}
    </div>
  )
}

type ExtraValueProps = {
  children: React.ReactNode
}

const ExtraValue: React.FC<ExtraValueProps> = (props) => {
  return (
    <span
      css={css`
        text-align: right;
        font-size: 13px;
        display: block;
        color: ${colors.gray90};
        ${mq.untilMedium} {
          margin-top: 3px;
          text-align: left;
        }
      `}
    >
      {props.children}
    </span>
  )
}
